import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { AllAccountSummaryGetResponse } from 'const/syfApiQueries'
import { DEEP_LINK_FEATURES } from 'const/deepLink'
import routes from 'const/routes'
import { selectedAccountAtom } from 'store/atoms'
import deepLinkStorage from 'helpers/deepLinkStorage'
import {
  isDeepLinkSession,
  isRewardsDeepLink,
  setDeepLinkStatus
} from 'helpers/deepLinkAnalytics'
/**
 * Handles post-login account selection for deep linked sessions.
 *
 * After authentication, `useAuthentication` stores a single `deepLinkClient` key
 * in sessionStorage. If a `clientId` param was present in the URL, that value is
 * stored; otherwise the `client` param value is stored.
 *
 * This hook waits for all account summary data to be fetched, finds the matching
 * account by `deepLinkClient`, selects it, and cleans up sessionStorage.
 */
const useDeepLinkNavigation = (
  allAccountSummaryData: AllAccountSummaryGetResponse[],
  isAllAccountSummaryFetched: boolean,
  isAllAccountSummaryError: boolean,
  isTokenFetched: boolean
): void => {
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isOnDashboard = pathname === routes.HOME
  // hasRun prevents re-execution when isOnDashboard changes due to route transitions
  const hasRun = useRef(false)

  useEffect(() => {
    // Guard to ensure this logic only runs once, even if dependencies change
    if (hasRun.current) return
    const targetClientId = deepLinkStorage.getClient()
    const targetFeature = deepLinkStorage.getFeature()

    if (!targetClientId) {
      if (isDeepLinkSession()) {
        hasRun.current = true
      }
      return
    }

    /*
     * Credit-view and payment-security deep-links are handled by dedicated hooks.
     * useCreditViewDeepLink: Selects first accessToCreditView-enabled account (not first clientId match); silent fail → HOME (not ERROR).
     * usePaymentSecurityDeepLink: Selects first accessToPaymentSecurity-enabled account; silent fail → HOME (not ERROR).
     * Custom Card Art deep link: useCustomCardDeepLink selects the sole
     * CareCredit account without matching clientId or requiring an offer.
     */
    if (
      targetFeature === DEEP_LINK_FEATURES.creditview ||
      targetFeature === DEEP_LINK_FEATURES.paymentsecurity ||
      targetFeature === DEEP_LINK_FEATURES.customcard
    ) {
      hasRun.current = true
      return
    }

    if (!isTokenFetched) return
    if (!isAllAccountSummaryFetched) return

    if (isAllAccountSummaryError) {
      deepLinkStorage.clearAll()
      hasRun.current = true
      navigate(routes.ERROR)
      return
    }
    /*
     * We are trying to find the account that matches the deepLinkClient. If we can't find it, we navigate to an error page. If we do find it, we set it as the selected account and then clean up sessionStorage.
     * find function returns the first matching element (or undefined if no match)
     */
    const matchedAccount = allAccountSummaryData.find(
      (account) =>
        account?.product?.clientId?.toLowerCase() ===
        targetClientId.toLowerCase()
    )

    if (!matchedAccount) {
      deepLinkStorage.clearAll()
      hasRun.current = true
      navigate(routes.ERROR)
      return
    }

    setSelectedAccount(matchedAccount.accountId)
    deepLinkStorage.clearAll()
    /*
     * For migrated RC→Universal accounts the OAuth state can be lost, landing
     * the user on the dashboard instead of the deep link target (e.g. /rewards).
     * Mark analytics as 'failure' so the Rewards page doesn't falsely record 'success'.
     */
    if (isRewardsDeepLink() && isOnDashboard) setDeepLinkStatus('failure')
    hasRun.current = true
  }, [
    allAccountSummaryData,
    isAllAccountSummaryFetched,
    isAllAccountSummaryError,
    isTokenFetched,
    navigate,
    setSelectedAccount,
    isOnDashboard
  ])
}

export default useDeepLinkNavigation
